import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const GENERIC = "__generic";

export const ChecklistEditor = ({ open, onOpenChange, template, businessUnits, serviceTypes, onSaved }) => {
  const [t, setT] = useState(null);
  const [newItem, setNewItem] = useState("");

  useEffect(() => {
    if (open) setT(template ? { ...template, items: template.items.map((i) => ({ ...i })) } : { name: "", business_unit_code: businessUnits[0]?.code, service_type_code: null, items: [], active: true });
  }, [open, template, businessUnits]);

  if (!t) return null;
  const setItems = (items) => setT({ ...t, items });
  const move = (i, d) => {
    const items = [...t.items];
    [items[i], items[i + d]] = [items[i + d], items[i]];
    setItems(items);
  };
  const addItem = () => {
    if (!newItem.trim()) return;
    setItems([...t.items, { label: newItem.trim(), required: true }]);
    setNewItem("");
  };

  const save = async () => {
    const body = { name: t.name, business_unit_code: t.business_unit_code, service_type_code: t.service_type_code, items: t.items, active: t.active };
    try {
      if (template) await api.patch(`/settings/checklists/${template.id}`, body);
      else await api.post("/settings/checklists", body);
      toast.success("Checklist guardado (las OS existentes conservan su versión)");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="checklist-editor-dialog">
        <DialogHeader><DialogTitle className="font-display">{template ? `Editar checklist · v${template.version}` : "Nuevo checklist"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs uppercase text-muted-foreground">Nombre</Label><Input value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} data-testid="checklist-name-input" /></div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase text-muted-foreground">Unidad de negocio</Label>
            <Select value={t.business_unit_code} onValueChange={(v) => setT({ ...t, business_unit_code: v })}>
              <SelectTrigger data-testid="checklist-bu-select"><SelectValue /></SelectTrigger>
              <SelectContent>{businessUnits.map((b) => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase text-muted-foreground">Tipo de servicio</Label>
            <Select value={t.service_type_code || GENERIC} onValueChange={(v) => setT({ ...t, service_type_code: v === GENERIC ? null : v })}>
              <SelectTrigger data-testid="checklist-st-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={GENERIC}>General (cualquier otro servicio)</SelectItem>
                {serviceTypes.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          {t.items.map((it, i) => (
            <div key={it.id || i} className="flex items-center gap-2 rounded-lg border border-border px-2 py-1.5" data-testid={`checklist-editor-item-${i}`}>
              <Input value={it.label} onChange={(e) => setItems(t.items.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className="h-9 flex-1 border-0 bg-transparent" />
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">Obligatorio<Switch checked={it.required} onCheckedChange={(v) => setItems(t.items.map((x, j) => (j === i ? { ...x, required: v } : x)))} /></label>
              <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === t.items.length - 1} onClick={() => move(i, 1)}><ArrowDown className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-400" onClick={() => setItems(t.items.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem())} placeholder="Nuevo punto de verificación" data-testid="checklist-new-item-input" />
            <Button variant="outline" onClick={addItem} data-testid="checklist-add-item-button"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={t.active} onCheckedChange={(v) => setT({ ...t, active: v })} />Activo</label>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button><Button onClick={save} data-testid="checklist-save-button">Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
